import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SalesTableState } from '@/components/sales/SalesTableState';
import { StatusChip, type ChipTone } from '@/components/sales/StatusChip';
import { useToast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import useAuth from '@/hooks/useAuth';
import { useBillingClients, useInvalidateBilling } from '@/hooks/useBilling';
import useClampPage from '@/hooks/useClampPage';
import useDebounce from '@/hooks/useDebounce';
import usePersistedState from '@/hooks/usePersistedState';
import { inr } from '@/lib/sales-metrics';
import { billingService, type BillingAssignmentFilter } from '@/services/billing.service';
import { errorMessage } from '@/utils/apiError';
import { BILLING_STATUSES, ROUTES } from '@/utils/constants';

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  ...BILLING_STATUSES.map((s) => ({ value: s, label: s })),
];

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'handedOverAt', label: 'Handover Date' },
  { value: 'clientName', label: 'Client Name' },
  { value: 'arn', label: 'ARN' },
  { value: 'total', label: 'Amount' },
  { value: 'status', label: 'Billing Stage' },
];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const ASSIGNMENT_TABS: { value: BillingAssignmentFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'My Work' },
  { value: 'unassigned', label: 'Unassigned' },
];

const billingTone = (s: string): ChipTone => {
  if (s === 'Billing Complete' || s === 'Partially Billed') return 'success';
  if (s === 'Pending Verification') return 'warning';
  return 'info';
};

const payTone = (s: string): ChipTone => {
  if (s === 'Full Payment') return 'success';
  if (s === 'Partial Payment') return 'warning';
  return 'neutral';
};

const pendingLabelFor = (iso: string | null): string => {
  if (!iso) return '-';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '-';
  const days = Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
  if (days === 0) return 'Today';
  return `${days} day${days === 1 ? '' : 's'}`;
};

const BillingClientsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const invalidateBilling = useInvalidateBilling();
  const meId = user?.id ?? 0;
  const [searchInput, setSearchInput] = usePersistedState('billingClients:search', '');
  const search = useDebounce(searchInput, 350);
  const [status, setStatus] = usePersistedState('billingClients:status', '');
  const [assignment, setAssignment] = usePersistedState<BillingAssignmentFilter>(
    'billingClients:assignment',
    'all',
  );
  const [sortBy, setSortBy] = usePersistedState('billingClients:sortBy', 'handedOverAt');
  const [sortDir, setSortDir] = usePersistedState<'asc' | 'desc'>('billingClients:sortDir', 'desc');
  const [page, setPage] = usePersistedState('billingClients:page', 1);
  const [limit, setLimit] = usePersistedState('billingClients:limit', 25);
  const [claimingId, setClaimingId] = useState<number | null>(null);

  const { data, isLoading, isError, refetch } = useBillingClients({
    search: search || undefined,
    status: status || undefined,
    assignment,
    sortBy,
    sortDir,
    page,
    limit,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  useClampPage(page, totalPages, !isLoading, setPage);
  const fromRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const toRow = Math.min(total, page * limit);
  // Rows are per ORDER (ADR-048), so both navigation and claiming address the
  // order, not just the client - a client can have two rows in this table.
  const goWorkflow = (rid: number, orderId: number) => {
    void navigate(ROUTES.BILLING_WORKFLOW(rid, orderId));
  };

  const claim = async (rid: number, orderId: number) => {
    setClaimingId(orderId);
    try {
      await billingService.claim(rid, orderId);
      toast.success('Billing task claimed. It is now in your queue.');
      invalidateBilling();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not claim this billing task'));
    } finally {
      setClaimingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 lg:h-full lg:min-h-0 lg:overflow-hidden">
      <div>
        <h1 className="text-lg font-bold text-foreground">Billing Queue</h1>
        <p className="text-sm text-muted-foreground">
          Onboarded clients handed over by Sales RM. Claim a task from the Unassigned pool to work
          it exclusively, or open one to start the billing process.
        </p>
      </div>

      <div className="inline-flex w-fit rounded-lg border border-border bg-muted/40 p-0.5">
        {ASSIGNMENT_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => {
              setAssignment(t.value);
              setPage(1);
            }}
            className={
              assignment === t.value
                ? 'rounded-md bg-card px-3 py-1.5 text-sm font-semibold text-foreground shadow-sm'
                : 'rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground'
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search name, ARN, business, RM, email…"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Sort</span>
          <div className="w-44">
            <Select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
              setPage(1);
            }}
          >
            {sortDir === 'asc' ? 'Asc' : 'Desc'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {STATUS_FILTERS.map((s) => (
          <Button
            key={s.value || 'all'}
            size="sm"
            variant={status === s.value ? 'default' : 'outline'}
            onClick={() => {
              setStatus(s.value);
              setPage(1);
            }}
          >
            {s.label}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page</span>
          <div className="w-20">
            <Select
              value={String(limit)}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              {ROWS_PER_PAGE_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {fromRow}–{toRow} of {total}
          </span>
          <Button
            variant="outline"
            size="icon"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col rounded-xl border border-border bg-card lg:min-h-0 lg:flex-1 lg:overflow-hidden">
        {isLoading && (
          <div className="flex h-full min-h-[320px] items-center justify-center py-14 text-sm text-muted-foreground">
            Loading billing queue…
          </div>
        )}
        {!isLoading && isError && (
          <SalesTableState
            variant="error"
            title="Couldn't load the billing queue"
            description="Something went wrong while fetching handed-over clients. Please try again."
            onRetry={() => {
              void refetch();
            }}
            className="h-full min-h-[320px]"
          />
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <SalesTableState
            variant="empty"
            title="No clients in the billing queue"
            description="Clients appear here once a Sales RM hands over a paid installment to billing."
            className="h-full min-h-[320px]"
          />
        )}
        {!isLoading && !isError && rows.length > 0 && (
          <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>ARN</TableHead>
                  <TableHead>Billing Stage</TableHead>
                  <TableHead>Sales RM</TableHead>
                  <TableHead>Assignee</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Handed Over At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={`${r.id}:${r.orderId}`}
                    className="cursor-pointer whitespace-nowrap"
                    onClick={() => goWorkflow(r.id, r.orderId)}
                  >
                    <TableCell className="font-medium">{r.leadCode ?? r.id}</TableCell>
                    <TableCell>
                      <p className="font-semibold">{r.clientName}</p>
                      {/* Which purchase this row bills - a returning client has
                          one row per order, otherwise they look duplicated. */}
                      <p className="text-xs text-muted-foreground">
                        {`Order #${r.orderNumber} · ${r.orderType} · ${r.email}`}
                      </p>
                    </TableCell>
                    <TableCell>{r.arn}</TableCell>
                    <TableCell>
                      <StatusChip tone={billingTone(r.billingStatus)}>{r.billingStatus}</StatusChip>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.rmName}</TableCell>
                    <TableCell>
                      {(() => {
                        if (r.assignedToId == null) {
                          if (!r.actionRequired) {
                            return <span className="text-xs text-muted-foreground">-</span>;
                          }
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={claimingId === r.orderId}
                              onClick={(e) => {
                                e.stopPropagation();
                                void claim(r.id, r.orderId);
                              }}
                            >
                              {claimingId === r.orderId ? 'Claiming…' : 'Claim'}
                            </Button>
                          );
                        }
                        return (
                          <StatusChip tone={r.assignedToId === meId ? 'info' : 'neutral'}>
                            {r.assignedToId === meId ? 'You' : (r.assignedToName ?? 'Claimed')}
                          </StatusChip>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{inr(r.total)}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(r.amountPaid)}</TableCell>
                    <TableCell>
                      <StatusChip tone={payTone(r.paymentStatus)}>{r.paymentStatus}</StatusChip>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {pendingLabelFor(r.handedOverAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingClientsPage;
